import {
  ImageFragment,
  TileContentChunkFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import type { ClassicProductDetailTile } from './classic-pdp-content';

export interface ClassicPdpUsp {
  text: string;
}

/** The parts of the section the editor maintains on the tile itself. */
export interface ClassicPdpTileContent {
  subtitle?: string;
  usps: ClassicPdpUsp[];
  compatibilityIcons: ImageFragment[];
  isRatingVisible: boolean;
}

// The trust lines live on the tile, not on the product: the product's shop
// features feed the chooser, and the two layouts are A/B arms that must not
// share editorial content.
function mapUsps(usps: TileContentChunkFragment | undefined): ClassicPdpUsp[] {
  return (usps?.chunks ?? []).reduce<ClassicPdpUsp[]>((result, chunk) => {
    const text = extractComponentFromChunk(chunk, 'text', 'SingleLineContent')?.text?.trim();

    if (!text) {
      return result;
    }

    return [...result, { text }];
  }, []);
}

/**
 * Maps the editable tile components onto the flat contract of the section.
 * They are the same for every item a tile links, product or bundle.
 */
export function mapClassicPdpTileContent(tile: ClassicProductDetailTile): ClassicPdpTileContent {
  return {
    subtitle: tile.subtitle?.text || undefined,
    usps: mapUsps(tile.usps),
    compatibilityIcons: (tile['compatibility-icons']?.images ?? []) as ImageFragment[],
    // An unset boolean means the editor never touched it, and the rating is
    // part of the default layout, so only an explicit `false` hides it.
    isRatingVisible: tile['show-rating']?.value !== false,
  };
}
